import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Picker from 'react-mobile-picker';
import { Button, useMediaQuery, Box } from '@mui/material';
import './scrollableDatePicker.css';
import { useTheme } from '@mui/material/styles';

const commonStyleForDialogueActionButtonText = {
  color: '#F15C30',
  fontSize: '22px',
  fontWeight: '500',
  padding: '0px 50px',
};

// Utility function to get days based on the year and month
function getDayArray( year, month, maxDay ) {
  const dayCount = new Date( year, month, 0 ).getDate();
  return Array.from( { length: Math.min( dayCount, maxDay ) }, ( _, i ) => String( i + 1 ).padStart( 2, '0' ) );
}

// Month names for the picker
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function ScrollableDatePicker( {
  onDateChange,
  initialDate,
  selctedTextcolor = '#000',
  nonSelctedTextColor = "#bcbcbc",
  selctedDateAreaBg = "#F5F5F5",
  isAimdFlow = false,
  isSmallDevice = false

} ) {

  const theme = useTheme();
  const isLargeScreen = useMediaQuery( theme.breakpoints.up( 'lg' ) );
  // Get today's date details
  const today = new Date();
  const currentDay = String( today.getDate() ).padStart( 2, '0' );
  const currentMonth = String( today.getMonth() + 1 ).padStart( 2, '0' ); // Months are 0-based
  const currentYear = String( today.getFullYear() );

  // Set the initial value of the picker based on the `initialDate` prop or default to 25th February 1998
  const [ pickerValue, setPickerValue ] = useState( {
    day: '25', // Default day set to 25
    month: '06', // Default month set to June
    year: '1998', // Default year set to 1998
  } );

  const [ formattedDate, setFormattedDate ] = useState( '' ); // Initially empty, shows placeholder


  // If initialDate is passed, set picker value based on the provided date
  useEffect( () => {
    const defaultDate = '1998-02-26'; // Default date if initialDate is not provided
    const dateToUse = initialDate || defaultDate; // Use initialDate if it exists, otherwise use defaultDate
    const parsedDate = new Date( dateToUse ); // Parse the selected or default date

    const initialDay = String( parsedDate.getDate() ).padStart( 2, '0' );
    const initialMonth = String( parsedDate.getMonth() + 1 ).padStart( 2, '0' );
    const initialYear = String( parsedDate.getFullYear() );

    setPickerValue( { day: initialDay, month: initialMonth, year: initialYear } );
    // Format displayed date as MM/DD/YYYY (changed from DD/MM/YYYY)
    setFormattedDate( `${ initialMonth }/${ initialDay }/${ initialYear }` );
  }, [ initialDate ] );

  // Handle change in picker value
  const handlePickerChange = useCallback(
    ( newValue, key ) => {
      const { day, month, year } = newValue;

      // Update the picker value
      let updatedPickerValue = { ...pickerValue, ...newValue };

      if ( key === 'month' || key === 'year' ) {
        // If the year or month changes, we need to update the days array
        const maxDay = year === currentYear && month === currentMonth ? currentDay : 31;
        const newDayArray = getDayArray( Number( updatedPickerValue.year ), Number( updatedPickerValue.month ), maxDay );

        // Ensure the selected day is valid for the new month
        if ( !newDayArray.includes( updatedPickerValue.day ) ) {
          updatedPickerValue = { ...updatedPickerValue, day: newDayArray[ 0 ] }; // Set to the first valid day
        }
      }

      setPickerValue( updatedPickerValue );

      // Format the selected date as MM/DD/YYYY (changed from DD/MM/YYYY)
      const newFormattedDate = `${ updatedPickerValue.month }/${ updatedPickerValue.day }/${ updatedPickerValue.year }`;
      setFormattedDate( newFormattedDate ); // Update the displayed date

      // Use updatedPickerValue to avoid using stale state
      // The formatted date for API should still be in YYYY-MM-DD format
      const formatted = `${ updatedPickerValue.year }-${ updatedPickerValue.month }-${ updatedPickerValue.day }T00:00:00`;

      onDateChange( formatted ); // Pass the formatted date to the parent
    },
    [ pickerValue, currentDay, currentMonth, currentYear ]
  );

  // Disable page scroll when interacting with the Picker
  const disablePageScroll = useCallback( ( e ) => {
    e.preventDefault(); // Prevent scrolling on the page
  }, [] );

  useEffect( () => {
    const pickerElement = document.querySelector( '.scrollable-date-picker' );

    if ( pickerElement ) {
      pickerElement.addEventListener( 'touchstart', () => {
        // Disable scrolling when the picker is being interacted with
        window.addEventListener( 'touchmove', disablePageScroll, { passive: false } );
      } );

      pickerElement.addEventListener( 'touchend', () => {
        // Re-enable scrolling when interaction ends
        window.removeEventListener( 'touchmove', disablePageScroll );
      } );
    }

    return () => {
      window.removeEventListener( 'touchmove', disablePageScroll );
    };
  }, [ disablePageScroll ] );

  return (
    <Box className="scrollable-date-picker">
      <Button
        sx={ {
          width: '100%',
          // height: '58px',
          height: isSmallDevice ? '30px' : '58px',
          borderRadius: isAimdFlow ? '30px' : '10px',
          background: selctedDateAreaBg,
          color: 'rgba(0, 0, 0, 0.50);',
          fontSize: isSmallDevice ? '12px' : '20px',
          fontWeight: '500',
          '&:hover': {
            background: selctedDateAreaBg //remove css which overirtes
          }
        } }
        type="button"
      >
        <Picker value={ pickerValue } onChange={ handlePickerChange } wheelMode={ isLargeScreen ? "off" : "normal" } height={ isLargeScreen ? 300 : isSmallDevice ? 130 : 180 } itemHeight={ isLargeScreen ? 40 : isSmallDevice ? 30 : 50 } style={ { display: "flex", justifyContent: "space-between", width: "100%" } }>
          {/* Modified order: Month first, then Day, then Year */ }
          <Picker.Column name="month">
            { monthNames
              .map( ( month, index ) => {
                const isFutureMonth = index + 1 > currentMonth && pickerValue.year === currentYear;
                return {
                  month,
                  index,
                  disabled: isFutureMonth
                };
              } )
              .filter( ( monthData ) => !monthData.disabled ) // Filter out the future months
              .map( ( monthData ) => (
                <Picker.Item
                  key={ monthData.month }
                  value={ String( monthData.index + 1 ).padStart( 2, '0' ) } // Internal value still numeric (01, 02, 03, ...)
                  itemHeight={ 50 }
                >
                  { ( { selected } ) => (
                    <div
                      className={ selected ? 'selected-item-style' : 'non-selected-item-style' }
                      style={ {
                        color: selected ? selctedTextcolor : nonSelctedTextColor,
                        fontSize: isSmallDevice ? '14px' : '20px'
                      } }
                    >
                      { monthData.month }
                    </div>
                  ) }
                </Picker.Item>
              ) ) }
          </Picker.Column>
          <Picker.Column name="day" >
            { getDayArray( Number( pickerValue.year ), Number( pickerValue.month ), currentYear === pickerValue.year && pickerValue.month === currentMonth ? currentDay : 31 ).map( ( day ) => (
              <Picker.Item key={ day } value={ day } itemHeight={ 50 } >
                { ( { selected } ) => (
                  <div className={ selected ? 'selected-item-style' : 'non-selected-item-style' }
                    style={ { color: selected ? selctedTextcolor : nonSelctedTextColor, fontSize: isSmallDevice ? "14px" : "20px" } }>
                    { day }
                  </div>
                ) }
              </Picker.Item>
            ) ) }
          </Picker.Column>
          <Picker.Column name="year">
            { Array.from( { length: today.getFullYear() - 1923 + 1 }, ( _, i ) => String( 1923 + i ) ).map( ( year ) => (
              <Picker.Item
                key={ year }
                value={ year }
                itemHeight={ 50 }
                disabled={ Number( year ) === today.getFullYear() } // Disable the current year
              >
                { ( { selected } ) => (
                  <div className={ selected ? 'selected-item-style' : 'non-selected-item-style' }
                    style={ { color: selected ? selctedTextcolor : nonSelctedTextColor, fontSize: isSmallDevice ? "14px" : "20px" } }
                  >{ year }</div>
                ) }
              </Picker.Item>
            ) ) }
          </Picker.Column>
        </Picker>
      </Button>
    </Box >
  )

}

ScrollableDatePicker.propTypes = {
  onDateChange: PropTypes.func.isRequired, // Callback function to send the selected date to the parent
  initialDate: PropTypes.string, // Optional initial date (YYYY-MM-DD)
};

export default ScrollableDatePicker;